import { useMyWalletAndAssets } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { toggleBalance } from "@/store/slices/settingsSlice";
import { aggregateLockedFunds } from "@/utils/aggregateLockedBalances";
import { formatCurrency } from "@/utils/formatCurrency";
import {
  ArrowLeftRight,
  Download,
  Eye,
  EyeOff,
  LockKeyhole,
  SendHorizontal,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const quickActions = [
  {
    label: "Deposit",
    icon: Download,
    path: "/dashboard/deposit",
  },
  {
    label: "Swap",
    icon: ArrowLeftRight,
    path: "/dashboard/swap",
  },
  {
    label: "Transfer",
    icon: SendHorizontal,
    path: "/dashboard/transfer",
  },
  {
    label: "Withdraw",
    icon: WalletCards,
    path: "/dashboard/withdraw",
  },
];

const WalletChip = () => {
  return (
    <div
      className="relative h-[34px] w-[46px] overflow-hidden rounded-[7px] border border-primary/40 bg-gradient-to-br from-primary/90 via-primary/55 to-primary/20 shadow-[0_5px_18px_rgba(0,0,0,0.35)] sm:h-[38px] sm:w-[51px]"
      aria-hidden="true"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-black/25" />

      <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-black/20" />
      <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-black/20" />

      <div className="absolute left-[9px] top-0 h-full w-px bg-black/15" />
      <div className="absolute right-[9px] top-0 h-full w-px bg-black/15" />

      <div className="absolute left-0 top-[9px] h-px w-full bg-black/15" />
      <div className="absolute bottom-[9px] left-0 h-px w-full bg-black/15" />

      <div className="absolute left-1/2 top-1/2 size-[15px] -translate-x-1/2 -translate-y-1/2 rounded-[3px] border border-black/25 bg-primary/20" />

      <div className="absolute left-1/2 top-1/2 size-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/20" />
    </div>
  );
};

const AvailableWallet = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { showBalance } = useAppSelector((state) => state.setting);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: myWallet } = useMyWalletAndAssets(user?.id!);

  return (
    <div className=" group relative h-[260px] w-full shrink-0 overflow-hidden rounded-[1.75rem] border border-primary/25 bg-[#090a0c] p-[1px] shadow-[0_25px_70px_-30px_hsl(var(--primary)/0.35)] transition-all duration-500 hover:shadow-[0_30px_85px_-25px_hsl(var(--primary)/0.45)]">
      <div
        className=" relative h-full w-full overflow-hidden rounded-[1.65rem] bg-[linear-gradient(135deg,#1b1c1f_0%,#101114_38%,#090a0c_100%)]
        "
      >
        {/* Metallic illumination */}
        <div className="pointer-events-none absolute -right-32 -top-40 h-96 w-96 rounded-full bg-primary/[0.22] blur-[70px] transition-transform duration-700 group-hover:scale-110" />

        <div className="pointer-events-none absolute -bottom-40 -left-32 h-80 w-80 rounded-full bg-primary/[0.08] blur-[65px]" />

        {/* Diagonal sheen */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.055)_0%,transparent_28%,transparent_65%,rgba(255,255,255,0.018)_100%)]" />

        {/* Fine texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg,rgba(255,255,255,.7) 0px,rgba(255,255,255,.7) 1px,transparent 1px,transparent 6px)",
          }}
        />

        {/* Top reflection */}
        <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        {/* Inner border */}
        <div className="pointer-events-none absolute inset-0 rounded-[1.65rem] border border-white/[0.075]" />

        {/* Watermark */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden select-none">
          <span className="whitespace-nowrap text-[5rem] font-black uppercase leading-none tracking-[-0.09em] text-white/[0.025] sm:text-[7rem] md:text-[9rem] lg:text-[10rem]">
            BuyFiat
          </span>
        </div>

        {/* Content */}
        <div className="relative z-10 p-5 sm:p-7 lg:p-8">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">
                Total Balance
              </p>

              <div className="mt-2 flex items-center gap-2">
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-40" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
                </span>

                <span className="text-[11px] font-medium text-white/35">
                  Available to spend
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => dispatch(toggleBalance())}
              aria-label={showBalance ? "Hide balance" : "Show balance"}
              className="flex size-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.055] text-white/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-md transition-all duration-200 hover:border-primary/25 hover:bg-white/[0.1] hover:text-white"
            >
              {showBalance ? (
                <Eye className="size-5" />
              ) : (
                <EyeOff className="size-5" />
              )}
            </button>
          </div>

          {/* Balance */}
          <div className="mt-5 flex items-end justify-between gap-4">
            <div className="min-w-0 flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-white/45">
                  {myWallet?.currency}
                </span>

                <h2 className="truncate text-3xl font-extrabold tracking-[-0.04em] text-white sm:text-4xl md:text-[2.75rem]">
                  {showBalance
                    ? formatCurrency(myWallet?.totalBalance ?? "")
                    : "••••••"}
                </h2>
              </div>
              {/* <span>Wallet Address</span> */}
            </div>

            <div className="shrink-0 pb-1">
              <WalletChip />
            </div>
          </div>

          {/* Quick actions */}
          <div className="mt-8 grid grid-cols-4 gap-2 sm:max-w-[520px] sm:gap-4">
            {quickActions.map(({ label, icon: Icon, path }) => (
              <button
                key={label}
                type="button"
                onClick={() => navigate(path)}
                className="group/action flex flex-col items-center gap-2"
              >
                <span className="flex size-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.055] text-white/60 transition-all duration-200 group-hover/action:-translate-y-0.5 group-hover/action:border-primary/25 group-hover/action:bg-primary/[0.12] group-hover/action:text-white sm:size-12">
                  <Icon className="size-[18px]" />
                </span>

                <span className="text-[11px] font-semibold text-white/40 sm:text-xs">
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Bottom reflection */}
        <div className="pointer-events-none absolute inset-x-10 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/35 to-transparent" />
      </div>
    </div>
  );
};

const LockedWallet = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { showBalance } = useAppSelector((state) => state.setting);

  const { data: myWallet } = useMyWalletAndAssets(user?.id!);

  const lockedFunds = aggregateLockedFunds(
    myWallet?.assets,
    myWallet?.totalBalanceCurrency ?? "USD",
  );

  const lockedBreakdown = [
    {
      label: "Trade",
      value: lockedFunds.trade,
    },
    {
      label: "Transfer",
      value: lockedFunds.transfer,
    },
    {
      label: "Withdrawal",
      value: lockedFunds.withdrawal,
    },
    {
      label: "Deposit",
      value: lockedFunds.deposit,
    },
  ];

  return (
    <div className="group relative h-[260px] w-full shrink-0 overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#0b0d11] p-[1px] shadow-[0_25px_70px_-30px_rgba(0,0,0,0.7)] transition-all duration-500 hover:border-white/15 hover:shadow-[0_30px_85px_-25px_rgba(0,0,0,0.85)]">
      <div className="relative h-full w-full overflow-hidden rounded-[1.65rem] bg-[linear-gradient(135deg,#171a21_0%,#0d1016_45%,#080a0e_100%)]">
        {/* Locked-wallet glow */}
        <div className="pointer-events-none absolute -right-40 -top-40 h-[420px] w-[420px] rounded-full bg-sky-500/[0.10] blur-[90px] transition-transform duration-700 group-hover:scale-110" />

        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[350px] w-[350px] rounded-full bg-indigo-500/[0.07] blur-[90px]" />

        {/* Security grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,.8) 1px, transparent 1px)
            `,
            backgroundSize: "32px 32px",
          }}
        />

        {/* Glass reflection */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.045),transparent_30%,transparent_70%,rgba(255,255,255,0.02))]" />

        {/* Border */}
        <div className="pointer-events-none absolute inset-0 rounded-[1.65rem] border border-white/[0.07]" />

        {/* Watermark */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden select-none">
          <span className="whitespace-nowrap text-[5rem] font-black uppercase leading-none tracking-[-0.09em] text-white/[0.018] sm:text-[7rem] md:text-[9rem] lg:text-[10rem]">
            LOCKED
          </span>
        </div>

        {/* Content */}
        <div className="relative z-10 p-5 sm:p-6 lg:p-7">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">
                Locked Funds
              </p>

              <div className="mt-1.5 flex items-center gap-2">
                <span className="flex size-5 items-center justify-center rounded-full border border-sky-400/15 bg-sky-400/[0.07]">
                  <LockKeyhole className="size-2.5 text-sky-400/70" />
                </span>

                <span className="text-[11px] font-medium text-white/35">
                  Temporarily unavailable
                </span>
              </div>
            </div>

            <div className="flex size-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.045] text-sky-400/60">
              <LockKeyhole className="size-4" />
            </div>
          </div>

          {/* Total */}
          <div className="mt-4 flex items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-white/40">
                  {lockedFunds.currency}
                </span>

                <h2 className="truncate text-3xl font-extrabold tracking-[-0.04em] text-white sm:text-4xl md:text-[2.75rem]">
                  {showBalance
                    ? formatCurrency(Number(myWallet?.totalLockedBalance))
                    : "••••••"}
                </h2>
              </div>
            </div>

            <p className="max-w-[180px] shrink-0 pb-1 text-right text-[11px] font-medium leading-tight text-white/25">
              Reserved by active operations
            </p>
          </div>

          {/* Breakdown */}
          <div className="mt-5 grid grid-cols-4 gap-2 sm:gap-3">
            {lockedBreakdown.map((item) => (
              <div
                key={item.label}
                className="min-w-0 rounded-xl border border-white/[0.07] bg-white/[0.035] px-3 py-2.5 backdrop-blur-md"
              >
                <p className="truncate text-[9px] font-bold uppercase tracking-[0.12em] text-white/25">
                  {item.label}
                </p>

                <p className="mt-1.5 truncate text-sm font-bold text-white/70">
                  {showBalance ? formatCurrency(item.value) : "••••"}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom reflection */}
        <div className="pointer-events-none absolute inset-x-10 bottom-0 h-px bg-gradient-to-r from-transparent via-sky-400/20 to-transparent" />
      </div>
    </div>
  );
};

const WalletCard = () => {
  return (
    <section className="w-full min-w-0">
      <div className="relative w-full min-w-0">
        <div
          className="wallet-scroll flex w-full min-w-0 gap-3 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth overscroll-x-contain pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          "
        >
          {/* Available Wallet */}
          <div className="min-w-0 shrink-0 snap-start basis-[calc(100%-24px)] sm:basis-[520px] lg:basis-[560px]">
            <AvailableWallet />
          </div>

          {/* Locked Wallet */}
          <div className="min-w-0 shrink-0 snap-start basis-[calc(100%-24px)] sm:basis-[520px] lg:basis-[560px]">
            <LockedWallet />
          </div>
        </div>

        {/* Mobile carousel navigation hint */}
        <div className="flex items-center justify-center gap-3 pt-1 sm:hidden">
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-5 rounded-full bg-primary" />
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/25" />
          </div>

          <span className="flex items-center gap-1 text-[10px] font-medium tracking-wide text-muted-foreground/60">
            Swipe
            <ArrowLeftRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </section>
  );
};

export default WalletCard;
